import React, { useImperativeHandle, useRef } from 'react';
import { useReactive } from 'ahooks';
import { Button, Dropdown, message } from 'kenshin';
import {
  SearchOutlined,
  AppstoreOutlined,
  DownloadOutlined,
  CopyOutlined,
  StarOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  buildImagePromptSetting,
  buildVideoPromptSetting,
  findImageModelByCode,
  findVideoModelByCode,
} from '../../config/models';
import { addAiSessionItem, refreshAiSessionItem } from '@/_serveice/aiCreation';
import InputArea from '../InputArea';
import ImagePreview from '../ImagePreview';
import VideoPreview from '../VideoPreview';
import { isVideoUrl, getVideoCoverUrl } from '@/_util/util';
import './index.less';

const MODES = {
  IMAGE: 'image',
  VIDEO: 'video',
};

// 获取当前员工ID
function getEmpId() {
  try {
    const emp = JSON.parse(localStorage.getItem('employeeDTO'));
    return emp?.empId;
  } catch {
    return null;
  }
}

const ChatArea = React.forwardRef((props, ref) => {
  const { mode, conversationId, conversationLoading, onModeChange, onToggleAssetLibrary, onSessionIdChange, onRefreshList } = props;
  const messagesEndRef = useRef();
  const pollingTimerRef = useRef(null);

  const state = useReactive({
    messages: [],
    loading: false,
    previewImage: null,
    previewVideo: null,
  });

  // 清理轮询定时器
  const clearPolling = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  useImperativeHandle(ref, () => ({
    clearMessages: () => {
      state.messages = [];
    },
    addMessage: (msg) => {
      state.messages = [...state.messages, msg];
    },
    // 加载历史消息（对接会话详情接口后调用）
    loadHistory: (items) => {
      const msgs = items.map((item, idx) => {
        const userMsg = {
          id: `user-${item.itemId || idx}`,
          role: 'user',
          content: JSON.parse(item.promptSetting || '{}').prompt || '',
          time: item.submitTime || '',
        };
        const assistantMsg = {
          id: `assistant-${item.itemId || idx}`,
          role: 'assistant',
          content: item.state === 2 ? `生成失败: ${item.errorMsg || '未知错误'}` : item.state === 0 ? '正在生成中，请稍候...' : '生成完成',
          time: item.finishTime || '',
          images: (item.outputList
              ?.filter((o) => o.resourceUrl)
              .map((o) => o.resourceUrl) || []).filter((u) => !isVideoUrl(u)),
          videos: (item.outputList
              ?.filter((o) => o.resourceUrl)
              .map((o) => o.resourceUrl) || []).filter((u) => isVideoUrl(u)),
          state: item.state, // 0-进行中 1-已完成 2-失败
          itemId: item.itemId,
          genType: item.genType,
        };
        return [userMsg, assistantMsg];
      }).flat();
      state.messages = msgs;

      // 对进行中的消息重新启动轮询
      items.forEach((item, idx) => {
        if (item.state === 0 && item.itemId) {
          const assistantMsgId = `assistant-${item.itemId || idx}`;
          startPolling(item.itemId, assistantMsgId, item.genType || 1);
        }
      });
    },
  }));

  // 轮询刷新生成项
  const startPolling = (itemId, assistantMsgId, genType) => {
    const poll = async () => {
      try {
        const res = await refreshAiSessionItem({ itemId });
        if (!res?.success || !res?.data) {
          // 刷新失败，停止轮询
          const msg = state.messages.find((m) => m.id === assistantMsgId);
          if (msg) {
            msg.content = '生成失败，请重试';
            msg.state = 2;
          }
          state.loading = false;
          return;
        }

        const itemData = res.data;
        const msg = state.messages.find((m) => m.id === assistantMsgId);
        if (!msg) {
          state.loading = false;
          return;
        }

        if (itemData.state === 0) {
          // 进行中，继续轮询
          pollingTimerRef.current = setTimeout(poll, 3000);
        } else if (itemData.state === 1) {
          // 已完成
          msg.content = '生成完成';
          msg.state = 1;
          const urls =
            itemData.outputList
              ?.filter((o) => o.resourceUrl)
              .map((o) => o.resourceUrl) || [];
          msg.images = urls.filter((u) => !isVideoUrl(u));
          msg.videos = urls.filter((u) => isVideoUrl(u));
          state.loading = false;
        } else if (itemData.state === 2) {
          // 失败
          msg.content = `生成失败: ${itemData.errorMsg || '未知错误'}`;
          msg.state = 2;
          state.loading = false;
        }
      } catch (err) {
        console.error('轮询失败:', err);
        const msg = state.messages.find((m) => m.id === assistantMsgId);
        if (msg) {
          msg.content = '生成失败，请重试';
          msg.state = 2;
        }
        state.loading = false;
      }
    };

    // 3秒后开始第一次轮询
    pollingTimerRef.current = setTimeout(poll, 3000);
  };

  // 发送消息
  const handleSend = async (data) => {
    const empId = getEmpId();
    if (!empId) {
      message.error('未获取到员工信息，请重新登录');
      return;
    }

    // 添加用户消息
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: data.text,
      time: new Date().toLocaleString(),
      images: data.images || [],
    };
    state.messages = [...state.messages, userMsg];
    state.loading = true;

    // 构建 promptSetting (JSON字符串)
    let promptSetting;
    let genType;

    if (data.mode === MODES.IMAGE) {
      genType = 1; // 1-图片
      const model = findImageModelByCode(data.params.modelCode);
      promptSetting = buildImagePromptSetting({
        modelCode: data.params.modelCode,
        prompt: data.text,
        number: data.params.quantity,
        size: data.params.size,
        width: data.params.width,
        height: data.params.height,
        images: data.images || [],
        setting: model?.hasSetting ? {} : undefined,
      });
    } else {
      genType = 2; // 2-视频
      const model = findVideoModelByCode(data.params.modelCode);
      promptSetting = buildVideoPromptSetting({
        modelCode: data.params.modelCode,
        prompt: data.text,
        ratio: data.params.ratio,
        duration: data.params.duration,
        resolution: data.params.resolution,
        referenceImages: data.images || [],
      });
    }

    // 先添加"正在生成中"消息
    const assistantMsgId = `assistant-${Date.now()}`;
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '正在生成中，请稍候...',
      time: new Date().toLocaleString(),
      images: [],
      videos: [],
      state: 0, // 0-进行中
    };
    state.messages = [...state.messages, assistantMsg];

    // 调用新增生成接口
    try {
      const res = await addAiSessionItem({
        empId,
        genType,
        promptSetting,
        sessionId: conversationId || null,
      });

      if (res?.success && res?.data) {
        const { itemId, sessionId } = res.data;

        // 通知父组件更新 sessionId（用于下次会话）
        if (sessionId) {
          onSessionIdChange?.(sessionId);
        }

        // 通知父组件：刷新左侧列表
        onRefreshList?.();

        // 启动轮询
        if (itemId) {
          startPolling(itemId, assistantMsgId, genType);
        }
      } else {
        message.error(res?.errorMessage || '生成失败');
        // 更新消息为失败状态
        const msg = state.messages.find((m) => m.id === assistantMsgId);
        if (msg) {
          msg.content = `生成失败: ${res?.errorMessage || '未知错误'}`;
          msg.state = 2;
        }
        state.loading = false;
      }
    } catch (err) {
      console.error('调用新增生成接口失败:', err);
      message.error('网络异常，请稍后重试');
      const msg = state.messages.find((m) => m.id === assistantMsgId);
      if (msg) {
        msg.content = '网络异常，请稍后重试';
        msg.state = 2;
      }
      state.loading = false;
    }
  };

  // 组件卸载时清理轮询
  React.useEffect(() => {
    return () => clearPolling();
  }, []);

  // 打开图片预览
  const handlePreviewImage = (imgUrl) => {
    state.previewImage = imgUrl;
  };

  // 打开视频预览
  const handlePreviewVideo = (videoUrl) => {
    state.previewVideo = videoUrl;
  };

  // 消息操作菜单
  const getMessageMenu = (msg) => (
    <Dropdown.Menu>
      <Dropdown.Item>
        <div>复制</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div>收藏</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div style={{ color: '#ff4d4f' }}>删除</div>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  // 渲染消息内容
  const renderMessageContent = (msg) => {
    if (msg.role === 'user') {
      return (
        <div className="userMessage">
          <div className="msgContent">{msg.content}</div>
          {msg.images && msg.images.length > 0 && (
            <div className="msgImages">
              {msg.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt=""
                  className="msgImage"
                  onClick={() => handlePreviewImage(img)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="assistantMessage">
        {msg.state === 0 && (
          <div className="msgLoading">
            <div className="loadingDot" />
            <div className="loadingDot" />
            <div className="loadingDot" />
            <span className="loadingText">正在生成中，请稍候...</span>
          </div>
        )}
        {msg.state === 1 && (
          <>
            <div className="msgContent">{msg.content}</div>
            {msg.images && msg.images.length > 0 && (
              <div className="msgImageGrid">
                {msg.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="msgImageItem"
                    onClick={() => handlePreviewImage(img)}
                  >
                    <img src={img} alt="" />
                    <div className="msgImageActions">
                      <DownloadOutlined title="下载" />
                      <CopyOutlined title="复制" />
                      <StarOutlined title="收藏" />
                      <EditOutlined title="编辑" />
                      <DeleteOutlined title="删除" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {msg.videos && msg.videos.length > 0 && (
              <div className="msgVideoGrid">
                {msg.videos.map((video, idx) => (
                  <div
                    key={idx}
                    className="msgVideoItem"
                    onClick={() => handlePreviewVideo(video)}
                  >
                    <img src={getVideoCoverUrl(video)} alt="" className="msgVideoCover" />
                    <div className="msgVideoPlayIcon">
                      <VideoCameraOutlined style={{ fontSize: 32, color: '#fff' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {msg.state === 2 && (
          <div className="msgError">
            <div className="msgContent">{msg.content}</div>
          </div>
        )}
        <Dropdown menu={getMessageMenu(msg)} trigger="click">
          <div className="msgActions">
            <CopyOutlined />
            <StarOutlined />
            <DeleteOutlined />
          </div>
        </Dropdown>
      </div>
    );
  };

  return (
    <div className="chatArea">
      {/* 顶部工具栏 */}
      <div className="chatHeader">
        <div className="headerLeft">
          <SearchOutlined className="headerIcon" />
          <AppstoreOutlined
            className="headerIcon"
            onClick={onToggleAssetLibrary}
          />
        </div>
      </div>

      {/* 消息列表 */}
      <div className="messageList">
        {/* 切换会话 loading */}
        {conversationLoading && (
          <div className="loadingState">
            <div className="loadingDot" />
            <div className="loadingDot" />
            <div className="loadingDot" />
            <span className="loadingText">加载会话中...</span>
          </div>
        )}

        {!conversationLoading && state.messages.length === 0 && !state.loading && (
          <div className="emptyState">
            <div className="emptyIcon">
              <PictureOutlined style={{ fontSize: 64, color: '#cbd5e1' }} />
            </div>
            <div className="emptyText">欢迎使用AI创作</div>
            <div className="emptySubText">选择下方模式，开始创作吧</div>
          </div>
        )}

        {!conversationLoading && state.messages.map((msg) => (
          <div key={msg.id} className="messageWrapper">
            {renderMessageContent(msg)}
          </div>
        ))}

        {!conversationLoading && state.loading && (
          <div className="loadingState">
            <div className="loadingDot" />
            <div className="loadingDot" />
            <div className="loadingDot" />
            <span className="loadingText">生成中...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 底部输入区 */}
      <InputArea mode={mode} onSend={handleSend} onModeChange={onModeChange} />

      {/* 图片预览弹窗 */}
      {state.previewImage && (
        <ImagePreview
          imageUrl={state.previewImage}
          onClose={() => (state.previewImage = null)}
        />
      )}

      {/* 视频预览弹窗 */}
      {state.previewVideo && (
        <VideoPreview
          videoUrl={state.previewVideo}
          onClose={() => (state.previewVideo = null)}
        />
      )}
    </div>
  );
});

export default ChatArea;
